'use client';

/**
 * Design System - Theme Control
 *
 * ÚNICO control de tema de la app (DS-5, ADR-0028 dec. 6): renderiza el
 * `theme-switcher` de la FAMILIA (`@/components/brand/theme-switcher`) en modo
 * CONTROLADO, cableado al `ThemeContext`. El cerebro sigue siendo el
 * `ThemeContext` (persistencia server-side por usuario + sync entre pestañas por
 * BroadcastChannel); la familia sólo pinta el control y avisa el cambio.
 *
 * Reemplaza a los toggles propios (`ThemeToggle` / `ThemeSwitcher`), ya retirados.
 */

import { ThemeSwitcher as FamilyThemeSwitcher } from '@/components/brand/theme-switcher';
import type { ThemeSwitcherVariant } from '@/components/brand/theme-switcher';
import { useTheme } from '../context/ThemeContext';
import { cn } from '@/lib/utils';

interface ThemeControlProps {
  /** Variante de la familia: 'icon' (toggle único) o 'segmented' (pastilla). */
  variant?: ThemeSwitcherVariant;
  /** Clases adicionales para el control. */
  className?: string;
}

export function ThemeControl({ variant = 'icon', className }: ThemeControlProps) {
  const { resolvedTheme, setTheme, isHydrated } = useTheme();

  // Antes de hidratar no conocemos el tema resuelto: mostramos un placeholder del
  // mismo tamaño para evitar layout shift (el color ya lo aplicó el script
  // anti-flash de next-themes por la clase `.dark`).
  if (!isHydrated || !resolvedTheme) {
    return (
      <div
        aria-hidden
        className={cn(
          variant === 'segmented' ? 'h-9 w-[132px]' : 'size-9',
          'rounded-sm',
          className,
        )}
      />
    );
  }

  return (
    <FamilyThemeSwitcher
      variant={variant}
      theme={resolvedTheme}
      onChange={(t) => setTheme(t)}
      className={className}
    />
  );
}
